import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TranslationPipe } from '../../../pipes/translation.pipe';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputMaskModule } from 'primeng/inputmask';
import { DateTime } from 'luxon';
import {
  CreatePetRequestDto,
  CustomerClient,
  CustomerDto,
  Gender, NeuteringStatus,
  PetBehavior,
  PetClient, Veterinarian
} from '../../../api/api-client';
import { FeedbackDataService } from '../../../services/feedback-data.service';
import { EditorModule } from 'primeng/editor';
import { CheckboxModule } from 'primeng/checkbox';
import { conditionalRequiredValidator } from '../../../validators/conditional-required.validator';
import { conditionalStringRequiredValidator } from '../../../validators/conditional-string-required.validator';
import { datetimeNotInFutureValidator } from '../../../validators/datetime-not-in-future.validator';
import { isFormControlInvalid } from '../../../util/form-control-invalid-checker';
import { SubscriptionManager } from '../../../services/subscription-manager.service';

@Component({
  selector: 'app-create-pet',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    DatePickerModule,
    InputMaskModule,
    SelectButtonModule,
    AutoCompleteModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    ButtonModule,
    TranslationPipe,
    EditorModule,
    CheckboxModule
  ],
  providers: [SubscriptionManager],
  templateUrl: './create-pet.component.html',
  styleUrl: './create-pet.component.scss'
})
export class CreatePetComponent implements OnInit {

  createPetForm = new FormGroup({
    owner: new FormControl<CustomerDto | null>(null, [Validators.required]),
    species: new FormControl<string>('', [Validators.required]),
    nickname: new FormControl<string>('', [Validators.required]),
    chipNumber: new FormControl<string>(''),
    race: new FormControl<string>('', [Validators.required]),
    color: new FormControl<string>(''),
    gender: new FormControl<Gender>('Unknown', [Validators.required]),
    dateOfBirth: new FormControl<Date>(DateTime.now().toJSDate(), datetimeNotInFutureValidator()),
    behavior: new FormControl<PetBehavior>('Unknown', [Validators.required]),
    behaviorNotes: new FormControl<string>(''),
    anamnesis: new FormControl<string>(''),
    neuteringStatus: new FormControl<NeuteringStatus>('Unknown', [Validators.required]),
    isInsured: new FormControl<boolean>(false),
    insuranceProvider: new FormControl<string | null>(null),
    insuranceNumber: new FormControl<string | null>(null),
    hasHomeVet: new FormControl<boolean>(false),
    homeVetName: new FormControl<string | null>(null),
    homeVetPhoneNumber: new FormControl<string | null>(''),
    hasBreederGivenName: new FormControl<boolean | null>(false),
    breederGivenName: new FormControl<string | null>(''),
    firstTreatmentDate: new FormControl<Date>(DateTime.now().toJSDate())
  });

  customers!: CustomerDto[];
  customersSuggestions!: CustomerDto[];
  species = ['Dog', 'Cat'];
  genders = ['Unknown', 'Female', 'Male'];
  behaviors = ['Unknown', 'PerfectlySafe', 'LowDanger', 'MediumDanger', 'HighDanger'];
  neuteredStatusForms: any[] = [];
  boolOptions: any[] = [];
  submitButtonClicked: boolean = false;
  calledFromDashboard = false;

  constructor(
    private readonly subscriptionManager: SubscriptionManager,
    private readonly customerService: CustomerClient,
    private readonly route: ActivatedRoute,
    private readonly petService: PetClient,
    private readonly router: Router,
    private readonly feedbackService: FeedbackDataService,
    private readonly translationPipe: TranslationPipe
  ) {
    this.neuteredStatusForms = [{
      label: this.translationPipe.transform('Pet.NeuteringStatus.Neutered'),
      value: 'Neutered'
    }, {
      label: this.translationPipe.transform('Pet.NeuteringStatus.NotNeutered'),
      value: 'NotNeutered'
    }, { label: this.translationPipe.transform('Pet.NeuteringStatus.Unknown'), value: 'Unknown' }];
    this.boolOptions = [{
      label: this.translationPipe.transform('Yes'),
      value: true
    }, { label: this.translationPipe.transform('No'), value: false }];

    this.createPetForm.get('insuranceProvider')?.setValidators(conditionalRequiredValidator(this.createPetForm, 'isInsured'));
    this.createPetForm.get('insuranceNumber')?.setValidators(conditionalRequiredValidator(this.createPetForm, 'isInsured'));
    this.createPetForm.get('homeVetName')?.setValidators(conditionalRequiredValidator(this.createPetForm, 'hasHomeVet'));
    this.createPetForm.get('homeVetPhoneNumber')?.setValidators([conditionalRequiredValidator(this.createPetForm, 'hasHomeVet'), Validators.pattern('[- +()0-9]{6,}')]);
    this.createPetForm.get('breederGivenName')?.setValidators(conditionalRequiredValidator(this.createPetForm, 'hasBreeder'));

    // Trigger validation when isInsured changes
    this.subscriptionManager.call(
      this.createPetForm.get('isInsured')?.valueChanges,
      (() => {
        this.createPetForm.get('insuranceProvider')?.updateValueAndValidity();
        this.createPetForm.get('insuranceNumber')?.updateValueAndValidity();
      })
    );

    // Trigger validation when hasHomeVet changes
    this.subscriptionManager.call(
      this.createPetForm.get('hasHomeVet')?.valueChanges,
      (() => {
        this.createPetForm.get('homeVetName')?.updateValueAndValidity();
        this.createPetForm.get('homeVetPhoneNumber')?.updateValueAndValidity();
      })
    );

    // Trigger validation when hasBreeder changes
    this.subscriptionManager.call(
      this.createPetForm.get('hasBreederGivenName')?.valueChanges,
      (() => {
        this.createPetForm.get('breederGivenName')?.updateValueAndValidity();
      })
    );
  }


  ngOnInit() {
    this.subscriptionManager.call(
      this.customerService.getAllCustomersForTenant(),
      (x => {
        this.customers = x.result;
        this.customersSuggestions = x.result;
        this.subscriptionManager.call(
          this.route.queryParams,
          (params => {
            this.createPetForm.patchValue({
              owner: this.customers.find(x => x.id == params['customerId']) ?? null
            });
            if (params['calledFromDashboard']) {
              this.calledFromDashboard = true;
            }
          })
        );
      })
    );
    this.feedbackService.feedbackData = null;
  }

  filterCustomers($event: AutoCompleteCompleteEvent) {
    this.customersSuggestions = this.customers.filter(x => (x.firstName + x.lastName).toLocaleLowerCase().includes($event.query.toLocaleLowerCase()));
  }

  getClassForBehavior(item: 'Unknown' | 'PerfectlySafe' | 'LowDanger' | 'MediumDanger' | 'HighDanger') {
    switch (item) {
      case 'Unknown':
        return ' bg-black w-8 h-8 rounded-full';
      case 'PerfectlySafe':
        return 'bg-pink-400 w-8 h-8 rounded-full';
      case 'LowDanger':
        return 'bg-green-400 w-8 h-8 rounded-full';
      case 'MediumDanger':
        return 'bg-yellow-400 w-8 h-8 rounded-full';
      case 'HighDanger':
        return 'bg-red-400 w-8 h-8 rounded-full';
    }
  }

  onSubmit(openCreateTreatmentPage: boolean) {
    this.submitButtonClicked = true;
    if (this.createPetForm.valid) {
      this.subscriptionManager.call(
        this.petService.createPet(
          <CreatePetRequestDto>{
            nickname: this.createPetForm.value.nickname ?? '',
            behavior: this.createPetForm.value.behavior ?? 'Unknown',
            color: this.createPetForm.value.color ?? '',
            race: this.createPetForm.value.race ?? '',
            species: this.createPetForm.value.species ?? '',
            gender: this.createPetForm.value.gender ?? 'Unknown',
            chipNumber: this.createPetForm.value.chipNumber?.replaceAll(/\D/gi, '') ?? '',
            dateOfBirth: DateTime.fromJSDate(this.createPetForm.value.dateOfBirth ?? new Date(Date.now())),
            ownerId: this.createPetForm.value.owner?.id,
            anamnesis: this.createPetForm.value.anamnesis ?? '',
            behaviorText: this.createPetForm.value.behaviorNotes ?? '',
            neuteringStatus: this.createPetForm.value.neuteringStatus ?? 'Unknown',
            isInsured: this.createPetForm.value.isInsured ?? false,
            insuranceProvider: this.createPetForm.value.insuranceProvider ?? '',
            insuranceNumber: this.createPetForm.value.insuranceNumber ?? '',
            hasHomeVet: this.createPetForm.value.hasHomeVet ?? false,
            hasBreederGivenName: this.createPetForm.value.hasBreederGivenName ?? false,
            breederGivenName: this.createPetForm.value.breederGivenName ?? '',
            homeVet: <Veterinarian><unknown>{
              name: this.createPetForm.value.homeVetName ?? '',
              phoneNumber: this.createPetForm.value.homeVetPhoneNumber ?? ''
            },
            firstTreatmentDate: DateTime.fromJSDate(this.createPetForm.value.firstTreatmentDate ?? new Date(Date.now()))
          }
        ),
        (x => {
          if (openCreateTreatmentPage) {

            this.router.navigate(['../loggedin/treatments/create'], {
              queryParams: {
                petId: x.result,
                ownerId: this.createPetForm.value.owner?.id
              }
            });
          } else if(this.calledFromDashboard){
            this.router.navigate(['../loggedin/pet-detail/' + x.result]);
          }
          else {
            this.router.navigate(['../loggedin/customer-detail/' + this.createPetForm.value.owner?.id]);
          }
        })
      );
    }
  }


  getOptionLabelForOwner(owner: CustomerDto): string {
    return owner.firstName + ' ' + owner.lastName + ', ' + owner.street + ' ' + owner.houseNumber + ', ' + owner.postalCode + ' ' + owner.city + ', ' + owner.country;
  }

  protected readonly isFormControlInvalid = isFormControlInvalid;
}
